import { formatISO } from 'date-fns';

const obtenerExtrasDesdeLista = (extrasLista = []) =>
  extrasLista.reduce(
    (acc, extra) => {
      const cantidad = Number(extra?.cantidad || 0);
      const total = Number(extra?.total || 0);

      if (Number.isFinite(cantidad)) {
        acc.cantidad += cantidad;
      }

      if (Number.isFinite(total)) {
        acc.monto += total;
      }

      return acc;
    },
    { monto: 0, cantidad: 0 }
  );

const normalizarNumero = (valor, decimales = 2) => {
  const numero = Number(valor || 0);
  return Number(numero.toFixed(decimales));
};

const obtenerExtrasResumen = (venta = {}) => {
  if (typeof venta.extrasSubtotal === 'number') {
    return {
      monto: Number(venta.extrasSubtotal),
      cantidad: Number(venta.extrasCantidad || 0)
    };
  }

  if (venta.extrasResumen && typeof venta.extrasResumen === 'object') {
    return {
      monto: Number(venta.extrasResumen.monto || 0),
      cantidad: Number(venta.extrasResumen.cantidad || 0)
    };
  }

  if (Array.isArray(venta.extras)) {
    return obtenerExtrasDesdeLista(venta.extras);
  }

  return { monto: 0, cantidad: 0 };
};

const obtenerTotalBase = (venta = {}, extrasMonto = null) => {
  if (typeof venta.totalBase === 'number') {
    return Number(venta.totalBase);
  }

  const extras = extrasMonto != null ? Number(extrasMonto) : obtenerExtrasResumen(venta).monto;
  const totalVentas = Number(venta.totalVentas || 0);
  const base = totalVentas - extras;
  return Number.isFinite(base) ? Math.max(Number(base.toFixed(2)), 0) : 0;
};

const obtenerExtrasCantidad = (venta = {}, extrasCantidad = null) => {
  if (typeof venta.extrasCantidad === 'number') {
    return Number(venta.extrasCantidad);
  }

  if (extrasCantidad != null) {
    return Number(extrasCantidad);
  }

  return obtenerExtrasResumen(venta).cantidad;
};

const crearMedio = (venta, medio, monto, indice) => {
  if (!medio && monto == null) return null;

  const id =
    medio?.id ??
    venta?.[`medio_pago_id${indice === 0 ? '' : `_${indice + 1}`}`] ??
    null;

  const nombre = medio?.nombre ||
    venta?.[`medioPago${indice === 0 ? '' : indice + 1}`] ||
    venta?.[`medio_pago_${indice === 0 ? '' : indice + 1}`]?.nombre ||
    (medio ? null : undefined);

  if (id == null && !nombre) {
    return null;
  }

  return {
    id,
    nombre: nombre || 'Medio no especificado',
    monto: Number(monto || 0)
  };
};

export const esMedioEfectivo = (nombre = '') => {
  const texto = String(nombre || '').toLowerCase();
  return texto.includes('efectivo') || texto.includes('cash');
};

const distribuirCantidad = (cantidad, ratio) => {
  const total = Number.isFinite(cantidad) ? cantidad : 0;
  const proporcion = Number.isFinite(ratio) ? ratio : 0;
  return proporcion <= 0 ? 0 : total * proporcion;
};

const asegurarMedioVenta = (venta) => {
  const medioPrincipal = crearMedio(venta, venta.medio_pago, venta.monto_medio_pago_1, 0);
  const medioSecundario = crearMedio(venta, venta.medio_pago_2, venta.monto_medio_pago_2, 1);

  const medios = [medioPrincipal, medioSecundario].filter(Boolean);

  if (medios.length === 0) {
    const total = Number(venta.totalVentas || 0);
    return [
      {
        id: 'sin-especificar',
        nombre: 'Medio no especificado',
        monto: total
      }
    ];
  }

  return medios;
};

export const agruparVentasPorMedio = (ventas = []) => {
  const medios = new Map();

  ventas.forEach((venta = {}) => {
    if (!venta) return;

    const extrasResumen = obtenerExtrasResumen(venta);
    const extrasMonto = Number(extrasResumen.monto || 0);
    const extrasCantidad = Number(obtenerExtrasCantidad(venta, extrasResumen.cantidad) || 0);
    const totalBase = obtenerTotalBase(venta, extrasMonto);
    const totalVenta = Number(venta.totalVentas || totalBase + extrasMonto || 0);
    const anulado = Boolean(venta.anulado);

    const mediosVenta = asegurarMedioVenta(venta);

    let totalMontos = mediosVenta.reduce(
      (acc, medio) => acc + (Number(medio.monto) > 0 ? Number(medio.monto) : 0),
      0
    );

    if (totalMontos <= 0) {
      if (mediosVenta.length > 0) {
        mediosVenta[0].monto = totalVenta;
        for (let i = 1; i < mediosVenta.length; i += 1) {
          mediosVenta[i].monto = 0;
        }
      }
      totalMontos = totalVenta;
    }

    const ratios = mediosVenta.map((medio, index) => {
      if (totalMontos <= 0) {
        return index === 0 ? 1 : 0;
      }
      const monto = Number(medio.monto) > 0 ? Number(medio.monto) : 0;
      return monto / totalMontos;
    });

    mediosVenta.forEach((medio, index) => {
      const ratio = ratios[index];
      const monto = Number(medio.monto || 0);
      const montoEntradas = normalizarNumero(totalBase * ratio);
      const montoExtras = normalizarNumero(extrasMonto * ratio);
      const extrasCantidadDistribuida = Math.round(distribuirCantidad(extrasCantidad, ratio));
      const clave = String(medio.id ?? medio.nombre ?? index);

      if (!medios.has(clave)) {
        medios.set(clave, {
          id: medio.id ?? null,
          nombre: medio.nombre || 'Medio no especificado',
          total: 0,
          entradasMonto: 0,
          extrasMonto: 0,
          extrasCantidad: 0,
          totalAnulado: 0,
          entradasMontoAnulado: 0,
          extrasMontoAnulado: 0,
          extrasCantidadAnulado: 0
        });
      }

      const acumulado = medios.get(clave);

      if (anulado) {
        acumulado.totalAnulado += monto;
        acumulado.entradasMontoAnulado += montoEntradas;
        acumulado.extrasMontoAnulado += montoExtras;
        acumulado.extrasCantidadAnulado += extrasCantidadDistribuida;
      } else {
        acumulado.total += monto;
        acumulado.entradasMonto += montoEntradas;
        acumulado.extrasMonto += montoExtras;
        acumulado.extrasCantidad += extrasCantidadDistribuida;
      }

      medios.set(clave, acumulado);
    });
  });

  const detalle = Array.from(medios.values())
    .map((medio) => ({
      ...medio,
      total: normalizarNumero(medio.total),
      entradasMonto: normalizarNumero(medio.entradasMonto),
      extrasMonto: normalizarNumero(medio.extrasMonto),
      totalAnulado: normalizarNumero(medio.totalAnulado),
      entradasMontoAnulado: normalizarNumero(medio.entradasMontoAnulado),
      extrasMontoAnulado: normalizarNumero(medio.extrasMontoAnulado),
      extrasCantidad: Math.round(medio.extrasCantidad || 0),
      extrasCantidadAnulado: Math.round(medio.extrasCantidadAnulado || 0)
    }))
    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' }));

  const acumulados = detalle.reduce(
    (acc, medio) => {
      acc.total += medio.total;
      acc.entradas += medio.entradasMonto;
      acc.extras += medio.extrasMonto;
      acc.totalAnulado += medio.totalAnulado;
      acc.entradasAnuladas += medio.entradasMontoAnulado;
      acc.extrasAnuladas += medio.extrasMontoAnulado;

      if (esMedioEfectivo(medio.nombre)) {
        acc.efectivo.total += medio.total;
        acc.efectivo.entradas += medio.entradasMonto;
        acc.efectivo.extras += medio.extrasMonto;
      } else {
        acc.otros.total += medio.total;
        acc.otros.entradas += medio.entradasMonto;
        acc.otros.extras += medio.extrasMonto;
      }

      return acc;
    },
    {
      total: 0,
      entradas: 0,
      extras: 0,
      totalAnulado: 0,
      entradasAnuladas: 0,
      extrasAnuladas: 0,
      efectivo: { total: 0, entradas: 0, extras: 0 },
      otros: { total: 0, entradas: 0, extras: 0 }
    }
  );

  return {
    detalle,
    totales: {
      total: normalizarNumero(acumulados.total),
      entradas: normalizarNumero(acumulados.entradas),
      extras: normalizarNumero(acumulados.extras),
      totalAnulado: normalizarNumero(acumulados.totalAnulado),
      entradasAnuladas: normalizarNumero(acumulados.entradasAnuladas),
      extrasAnuladas: normalizarNumero(acumulados.extrasAnuladas),
      efectivo: {
        total: normalizarNumero(acumulados.efectivo.total),
        entradas: normalizarNumero(acumulados.efectivo.entradas),
        extras: normalizarNumero(acumulados.efectivo.extras)
      },
      otros: {
        total: normalizarNumero(acumulados.otros.total),
        entradas: normalizarNumero(acumulados.otros.entradas),
        extras: normalizarNumero(acumulados.otros.extras)
      }
    }
  };
};

export const calcularTotalesFinancieros = (ventas = []) => {
  let ingresosEntradas = 0;
  let ingresosExtras = 0;
  let ingresosTotales = 0;
  let cantidadExtras = 0;

  ventas.forEach((venta = {}) => {
    if (!venta || venta.anulado) return;

    const extrasResumen = obtenerExtrasResumen(venta);
    const extrasMonto = Number(extrasResumen.monto || 0);
    const extrasCantidad = Number(extrasResumen.cantidad || 0);
    const base = obtenerTotalBase(venta, extrasMonto);
    const totalVenta = Number(venta.totalVentas || base + extrasMonto || 0);

    ingresosEntradas += base;
    ingresosExtras += extrasMonto;
    ingresosTotales += totalVenta;
    cantidadExtras += extrasCantidad;
  });

  return {
    entradas: normalizarNumero(ingresosEntradas),
    extras: normalizarNumero(ingresosExtras),
    totalGeneral: normalizarNumero(ingresosTotales),
    extrasCantidad: Math.round(cantidadExtras || 0)
  };
};

export const combinarDesglosesMedios = (listas = []) => {
  const registros = [];

  listas.forEach((item) => {
    if (!item) return;
    if (Array.isArray(item)) {
      registros.push(...item);
    } else if (Array.isArray(item.registros)) {
      registros.push(...item.registros);
    }
  });

  return agruparVentasPorMedio(registros);
};

export const combinarTotalesFinancieros = (listas = []) => {
  const registros = [];

  listas.forEach((item) => {
    if (!item) return;
    if (Array.isArray(item)) {
      registros.push(...item);
    } else if (Array.isArray(item.registros)) {
      registros.push(...item.registros);
    }
  });

  return calcularTotalesFinancieros(registros);
};

export const formatearFechaISO = (fecha) => {
  try {
    return formatISO(new Date(fecha), { representation: 'date' });
  } catch (error) {
    return '';
  }
};

